import { SimpleCarousel } from '@components'
import { sponsors } from '@constants'
import WestHamBanner from '@images/promo/West Ham - 2.jpg'
import { isValidArray, tr } from 'mmfintech-commons'
import type { ReactNode } from 'react'
import styles from './AuthLeftSection.module.scss'
import promotionalSlides from './slides'

export type AuthLeftSectionProps = {
  /**
   * Display mode for the left section
   * - 'carousel': Shows the promotional carousel (default)
   * - 'static': Shows the static image with welcome text
   * @default 'static'
   */
  displayMode?: 'carousel' | 'static'

  /**
   * Title text for static mode
   * Only used when displayMode is 'static'
   */
  title?: ReactNode

  /**
   * Subtitle text for static mode
   * Only used when displayMode is 'static'
   */
  subtitle?: ReactNode

  /**
   * Callback when carousel slide changes
   * Only used when displayMode is 'carousel'
   */
  onSlideChange?: (slideIndex: number) => void

  welcomeTexts?: boolean
}

/**
 * AuthLeftSection Component
 *
 * A backward-compatible component that supports both:
 * - Carousel mode: Shows promotional carousel (default)
 * - Static mode: Shows static banner image with welcome text
 *
 * This component was created to preserve both static image and carousel flows,
 * making it easy to switch between the two modes as needed.
 *
 * @example
 * // Carousel mode (default)
 * <AuthLeftSection onSlideChange={handleSlideChange} />
 *
 * @example
 * // Static mode with welcome texts
 * <AuthLeftSection
 *   title="Welcome to The Kingdom Bank"
 *   subtitle="Banking for the modern world"
 *   welcomeTexts={true}
 * />
 *
 * @example
 * // Carousel mode
 * <AuthLeftSection displayMode="carousel" onSlideChange={handleSlideChange} />
 */
const AuthLeftSection = (props: AuthLeftSectionProps) => {
  const { displayMode = 'static', title, subtitle, onSlideChange, welcomeTexts = false } = props

  if (displayMode === 'carousel') {
    return (
      <div className={styles['auth-left-section-carousel']}>
        <div className={styles['auth-promo-carousel-wrapper']}>
          <SimpleCarousel
            items={promotionalSlides.map(slide => ({
              id: slide.id,
              content: slide.card
            }))}
            onSlideChange={onSlideChange}
          />
        </div>
      </div>
    )
  }

  const sponsorsList = sponsors()

  // Static mode (old implementation)
  return (
    <div className={styles['auth-left-section-static']}>
      <div />

      <img
        className='coins-banner'
        src={WestHamBanner}
        alt={tr('FRONTEND.AUTH_LEFT_SECTION.WEST_HAM_UNITED_BANNER', 'West Ham United banner')}
      />

      {welcomeTexts && (
        <div className='auth-welcome-text'>
          <h1 className='auth-welcome-title'>{title}</h1>
          <p className='auth-welcome-subtitle'>{subtitle}</p>
          {isValidArray(sponsorsList) && (
            <div className='auth-welcome-sponsor-wrapper'>
              <span className='sponsor-wrapper-title'>{tr('FRONTEND.SPONSORS.TITLE', 'Official sponsor of')}</span>
              <div className='auth-welcome-sponsors-container'>
                {sponsorsList.map(({ title, Image }) => (
                  <div key={title} className='auth-welcome-sponsors'>
                    <Image />
                    <span className='text'>{title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AuthLeftSection
